/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 20/04/22
 */

import React, { useLayoutEffect, useState } from 'react';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { Text, VStack } from '@chakra-ui/react';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { User } from '@/wpt-lib/interfaces/User';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import SupervisorListTable from '@/components/pages/Admin/Supervisor/SupervisorListTable';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { FormControlWrapperWithLabel } from '@/components/FormInputField/FormControlWrapper';
import UserSearch from '@/components/pages/Admin/Supervisor/UserSearch';
import { Supervisor } from '@/wpt-lib/interfaces/Supervisor';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import { Tile } from '@/components/Tile';
import { AxiosResponse } from 'axios';

export interface SupervisorEditorProps {
    assignmentId: number;
    inactive: boolean;
}

interface UpdateSupervisorsDTO {
    supervisorUserIds: number[];
}

export interface UserWithAutoAssigned extends User {
    isAutoAssigned: boolean;
}

const SupervisorEditor = (props: SupervisorEditorProps) => {
    const { currentAssignment } = useAssignment();

    const { error, loading, responseData: supervisors, makeRequest } = useGetRequest<ListResponse<Supervisor>>();
    const {
        error: submitSupervisorsError,
        loading: submittingSupervisors,
        makeRequest: updateSupervisors,
        responseData: updatedSupervisors,
    } = usePutRequest<Supervisor[], UpdateSupervisorsDTO>();

    const [tempSupervisorList, setTempSupervisorList] = useState<UserWithAutoAssigned[]>();

    const [hasUpdate, setUpdated] = useState(false);

    useAsyncLayoutEffect(async () => {
        try {
            setUpdated(false);
            await makeRequest(`assignment/${props.assignmentId}/supervisors`, {
                params: {
                    assignment: currentAssignment?.id,
                },
            });
        } catch (e) {
            // handled by hook
        }
    }, [props.assignmentId, currentAssignment]);

    useLayoutEffect(() => {
        if (hasUpdate && updatedSupervisors) {
            setTempSupervisorList(
                updatedSupervisors.map((s) => ({
                    ...s.user,
                    isAutoAssigned: s.isAutoAssigned,
                })),
            );
        } else if (supervisors) {
            setTempSupervisorList(
                supervisors.items.map((s) => ({
                    ...s.user,
                    isAutoAssigned: s.isAutoAssigned,
                })),
            );
        }
    }, [supervisors, updatedSupervisors, hasUpdate]);

    const handleSubmitSupervisors = async (newSupervisors: UserWithAutoAssigned[]) => {
        try {
            await updateSupervisors(
                `assignment/${props.assignmentId}/supervisors`,
                { supervisorUserIds: newSupervisors.map((u) => u.id) },
                {
                    params: {
                        assignment: currentAssignment?.id,
                    },
                },
            );
            setUpdated(true);
        } catch (e) {
            // handled by hook
        }
    };

    const onChooseUser = async (user: User) => {
        if (!tempSupervisorList || !tempSupervisorList.find((u) => u.id === user.id)) {
            await handleSubmitSupervisors([...(tempSupervisorList || []), { ...user, isAutoAssigned: false }]);
        }
    };

    const onRemoveUser = async (user: User) => {
        await handleSubmitSupervisors((tempSupervisorList || []).filter((u) => u.id !== user.id));
    };

    let putErrorMessage: string | null = null;
    if (submitSupervisorsError) {
        const { response } = submitSupervisorsError as { response?: AxiosResponse };
        if (response && response.status === 409 && response.data?.message) putErrorMessage = response.data?.message;
    }

    return (
        <Tile colspan={2} collapsable overflowX="auto" heading={'Supervisors'} defaultOpen>
            <VStack alignItems={'flex-start'} spacing={3}>
                <Text>
                    This section allows you to add additional supervisors to this individual assignment, in addition to
                    their supervisor loaded from NEO.
                </Text>
                <StateDisplay
                    data={tempSupervisorList}
                    loading={loading}
                    error={!!error}
                    errorRender={() => <SimpleAlert variant="subtle">Could not load supervisors</SimpleAlert>}
                    render={(data) => {
                        const noSupervisor = !tempSupervisorList?.length;
                        return (
                            <>
                                <FormControlWrapperWithLabel label="Add Supervisor" labelProps={{ fontWeight: 'bold' }}>
                                    <UserSearch onSelect={onChooseUser} />
                                </FormControlWrapperWithLabel>
                                <SupervisorListTable
                                    supervisors={data}
                                    onRemove={onRemoveUser}
                                    actionsDisabled={submittingSupervisors || tempSupervisorList?.length === 1}
                                />
                                {noSupervisor && !props.inactive && (
                                    <SimpleAlert variant="subtle">At least one Supervisor is required.</SimpleAlert>
                                )}
                                {submitSupervisorsError && (
                                    <SimpleAlert variant="subtle">
                                        {putErrorMessage ?? 'Could not update supervisors.'}
                                    </SimpleAlert>
                                )}
                            </>
                        );
                    }}
                />
            </VStack>
        </Tile>
    );
};

export default SupervisorEditor;
